import { ZERO } from '../../dataset/constant/Common'
import { RowFlex } from '../../dataset/enum/Row'
import {
  IComputeRowListPayload,
  IDrawFloatPayload,
  IDrawOption,
  IDrawPagePayload,
  IDrawRowPayload,
  IGetOriginValueOption,
  IGetValueOption
} from '../../interface/Draw'
import { IEditorData, IEditorOption, IUpdateOption } from '../../interface/Editor'
import { IElement, IElementMetrics } from '../../interface/Element'
import { deepClone } from '../../utils'
import { Position } from '../position/Position'
import { Background } from './frame/Background'
import { Highlight } from './richtext/Highlight'
import { Margin } from './frame/Margin'
import { Strikeout } from './richtext/Strikeout'
import { Underline } from './richtext/Underline'
import { ElementType } from '../../dataset/enum/Element'
import { ImageParticle } from './particle/ImageParticle'
import { LaTexParticle } from './particle/latex/LaTexParticle'
import { TextParticle } from './particle/TextParticle'
import { PageNumber } from './frame/PageNumber'
import { TableParticle } from './particle/table/TableParticle'
import { TablePaging } from './particle/table/TablePaging'
import { HyperlinkParticle } from './particle/HyperlinkParticle'
import { LabelParticle } from './particle/LabelParticle'
import { Header } from './frame/Header'
import { SuperscriptParticle } from './particle/Superscript'
import { SubscriptParticle } from './particle/Subscript'
import { SeparatorParticle } from './particle/Separator'
import { Watermark } from './frame/Watermark'
import { WatermarkLayer } from '../../dataset/enum/Watermark'
import {
  EditorMode,
  EditorZone,
  PageMode,
  PaperDirection,
  WordBreak
} from '../../dataset/enum/Editor'
import {
  deleteSurroundElementList,
  getIsBlockElement,
  pickSurroundElementList,
  zipElementList
} from '../../utils/element'
import { CheckboxParticle } from './particle/CheckboxParticle'
import { RadioParticle } from './particle/RadioParticle'
import { DeepRequired, IPadding } from '../../interface/Common'
import {
  ControlComponent,
  ControlIndentation
} from '../../dataset/enum/Control'
import { formatElementList } from '../../utils/element'
import { DateParticle } from './particle/date/DateParticle'
import { IMargin } from '../../interface/Margin'
import { BlockParticle } from './particle/block/BlockParticle'
import { I18n } from '../i18n/I18n'
import { ImageObserver } from '../observer/ImageObserver'
import { Footer } from './frame/Footer'
import { TEXTLIKE_ELEMENT_TYPE } from '../../dataset/constant/Element'
import { ListParticle } from './particle/ListParticle'
import { Placeholder } from './frame/Placeholder'
import { Group } from './interactive/Group'
import { ImageDisplay } from '../../dataset/enum/Common'
import { PUNCTUATION_REG } from '../../dataset/constant/Regular'
import { LineBreakParticle } from './particle/LineBreakParticle'
import { LineNumber } from './frame/LineNumber'
import { PageBorder } from './frame/PageBorder'
import { jsPDF, Context2d } from 'jspdf'
import { IRow, IRowElement } from '../../interface/Row'
import { IColumnLayout, IColumnOption } from '../../interface/Column'
import { ColumnManager } from './column/ColumnManager'
import { ITd } from '../../interface/table/Td'
import { mergeOption } from '../../utils/option'
import { shrinkColgroupToWidth } from '../../utils/table'
import { Area } from './interactive/Area'
import { Graffiti } from './graffiti/Graffiti'
import { Badge } from './frame/Badge'
import { platform } from '../../platform/current'
// import { Draw } from '@hufe921/canvas-editor/dist/src/editor/core/draw/Draw'
// import { IEditorData } from '@hufe921/canvas-editor'
// import { ITd } from '@hufe921/canvas-editor/dist/src/editor/interface/table/Td'
// import { IRow, IRowElement } from '@hufe921/canvas-editor/dist/src/editor/interface/Row'

/**
 * Where to fetch the default fonts from when `loadDefaultFonts: true`.
 *
 *   - `'cdn'` (browser default): fetch TTFs from cdn.jsdelivr.net. Requires
 *     network access; works in both browser and Node.
 *   - `'bundled'` (node default): read TTFs from the package's dist/font/
 *     directory. Resolved relative to the running module — no network needed.
 *   - `{ dir }`: read TTFs from an absolute filesystem path that the consumer
 *     supplies. Useful for custom font installs in Node deployments.
 */
export type FontSource = 'cdn' | 'bundled' | { dir: string }

export type PdfOptions = {
  loadDefaultFonts?: boolean
  fontSource?: FontSource
}

export class DrawPdf {
  // private draw: Draw
  private fakeCanvas: HTMLCanvasElement
  private fakeCtx: CanvasRenderingContext2D
  // private container: HTMLDivElement
  private pageList: any[]
  private ctxListInfos: any[]
  private pageNo: number
  private mode: EditorMode
  private options: DeepRequired<IEditorOption>
  private position: Position
  private elementList: IElement[]
  private pdf!: jsPDF
  private fontCache: Array<{
    fileName: string
    base64: string
    id: string
    type: string
  }> = []
  private fontSource: FontSource = 'cdn'

  private i18n: I18n
  private margin: Margin
  private background: Background
  private badge: Badge
  private group: Group
  private area: Area
  private underline: Underline
  private strikeout: Strikeout
  private highlight: Highlight
  private imageParticle: ImageParticle
  private laTexParticle: LaTexParticle
  private textParticle: TextParticle
  private tableParticle: TableParticle
  private tablePaging: TablePaging
  private pageNumber: PageNumber
  private lineNumber: LineNumber
  private waterMark: Watermark
  private placeholder: Placeholder
  private header: Header
  private footer: Footer
  private hyperlinkParticle: HyperlinkParticle
  private labelParticle: LabelParticle
  private dateParticle: DateParticle
  private separatorParticle: SeparatorParticle
  // private pageBreakParticle: PageBreakParticle
  private superscriptParticle: SuperscriptParticle
  private subscriptParticle: SubscriptParticle
  private checkboxParticle: CheckboxParticle
  private radioParticle: RadioParticle
  private blockParticle: BlockParticle
  private listParticle: ListParticle
  private lineBreakParticle: LineBreakParticle
  // private control: Control
  private pageBorder: PageBorder
  private imageObserver: ImageObserver
  private graffiti: Graffiti

  private LETTER_REG: RegExp
  private WORD_LIKE_REG: RegExp
  private rowList: IRow[]
  private pageRowList: IRow[][]
  private printModeData: Required<Omit<IEditorData, 'graffiti'>> | null
  private controlMinWidthPlaceholderElementListSet: WeakSet<IElement[]>
  private columnManager: ColumnManager

  public defaultFontsLoadedPromise: Promise<boolean>

  constructor(
    // canvas-editor's IEditorOption is a separate copy — and, when consumed
    // from the editor's own source, a different module instance entirely — so
    // its enum-typed fields (mode, pageMode, …) are nominally incompatible and
    // no type union can accept `editor.command.getValue().options`. Options are
    // normalized by mergeOption at the boundary, so accept a loose object here;
    // pass this lib's IEditorOption for full autocomplete. (`object` rather
    // than Record<string, unknown> because interface types have no implicit
    // index signature and aren't assignable to Record.)
    options: IEditorOption | object,
    data: IEditorData,
    pdfOptions: PdfOptions = {}
    // draw: Draw
  ) {
    // this.draw = draw
    this.fakeCanvas = platform.createMeasurementCanvas()
    this.fakeCtx = this.fakeCanvas.getContext('2d')!
    // this.container = draw.getContainer()
    this.pageList = []
    this.ctxListInfos = []
    this.pageNo = 0
    this.options = mergeOption(options as IEditorOption)
    this.mode = this.options.mode
    data = data as IEditorData
    let headerElementList: IElement[] = []
    let mainElementList: IElement[] = []
    let footerElementList: IElement[] = []
    data = deepClone(data)
    if (Array.isArray(data)) {
      mainElementList = data
    } else {
      headerElementList = data.header || []
      mainElementList = data.main
      footerElementList = data.footer || []
    }
    const pageComponentData = [
      headerElementList,
      mainElementList,
      footerElementList
    ]
    pageComponentData.forEach(elementList => {
      formatElementList(elementList, {
        editorOptions: this.getOptions(),
        isForceCompensation: true
      })
    })
    pageComponentData.forEach(datas => {
      if (datas) {
        datas = this.filterAssistElement(datas)
      }
    })
    this.elementList = data.main

    this._formatContainer()
    this._resetPdf()

    this.fontSource = pdfOptions.fontSource ?? platform.defaultFontSource

    if (pdfOptions.loadDefaultFonts) {
      this.defaultFontsLoadedPromise = this._addDefaultFont()
    } else {
      this.defaultFontsLoadedPromise = Promise.resolve(false)
    }

    this.i18n = new I18n()
    this.position = new Position(this)
    // this.zone = new Zone(this.draw)
    // this.range = new RangeManager(this)
    this.margin = new Margin(this)
    this.background = new Background(this)
    this.badge = new Badge(this)
    this.group = new Group(this)
    this.area = new Area(this)
    this.underline = new Underline(this)
    this.strikeout = new Strikeout(this)
    this.highlight = new Highlight(this)
    this.imageParticle = new ImageParticle(this)
    this.laTexParticle = new LaTexParticle(this)
    this.textParticle = new TextParticle(this)
    this.tableParticle = new TableParticle(this)
    this.tablePaging = new TablePaging(this)
    this.pageNumber = new PageNumber(this)
    this.lineNumber = new LineNumber(this)
    this.waterMark = new Watermark(this)
    this.placeholder = new Placeholder(this)
    this.header = new Header(this, data.header)
    this.footer = new Footer(this, data.footer)
    this.hyperlinkParticle = new HyperlinkParticle(this)
    this.labelParticle = new LabelParticle(this)
    this.dateParticle = new DateParticle(this)
    this.separatorParticle = new SeparatorParticle(this)
    // this.pageBreakParticle = new PageBreakParticle(this)
    this.superscriptParticle = new SuperscriptParticle()
    this.subscriptParticle = new SubscriptParticle()
    this.checkboxParticle = new CheckboxParticle(this)
    this.radioParticle = new RadioParticle(this)
    this.blockParticle = new BlockParticle(this)
    this.listParticle = new ListParticle(this)
    this.lineBreakParticle = new LineBreakParticle(this)
    // this.control = new Control(this)
    this.pageBorder = new PageBorder(this)
    this.graffiti = new Graffiti(this, data.graffiti)
    this.columnManager = new ColumnManager(this)

    this.imageObserver = new ImageObserver()

    const { letterClass } = this.options
    this.LETTER_REG = new RegExp(`[${letterClass.join('')}]`)
    this.WORD_LIKE_REG = new RegExp(
      `${letterClass.map(letter => `[^${letter}][${letter}]`).join('|')}`
    )
    this.rowList = []
    this.pageRowList = []
    this.printModeData = null
    this.controlMinWidthPlaceholderElementListSet = new WeakSet()
  }

  public getCtx2d(): Context2d {
    return this.pdf.context2d
  }

  public getFakeCtx() {
    return this.fakeCtx
  }

  public measureText(font: string, text: string): TextMetrics {
    this.fakeCtx.save()
    this.fakeCtx.font = font
    const textMetrics = this.fakeCtx.measureText(text)
    this.fakeCtx.restore()
    return textMetrics
  }

  /**
   * Load a single TTF font and register it with the PDF. In the browser, `source`
   * must be an http(s) URL. In Node, `source` may be either an https URL in the
   * font allowlist or an absolute filesystem path — see src/platform/node.ts for
   * the SSRF/path-traversal checks applied.
   */
  public async downloadFont(
    source: string,
    fileName: string,
    id: string,
    type: string
  ) {
    const base64 = await platform.loadFontAsBase64(source)
    const font = { fileName, base64, id, type }
    this.fontCache.push(font)
    this._applyFont(this.pdf, font)
    // Hook for the Node platform to register the same bytes with @napi-rs/canvas
    // so fakeCtx.measureText sees the right glyph metrics. No-op in browser.
    platform.registerFontForMeasurement(font, base64)
    return base64
  }

  private _applyFont(
    pdf: jsPDF,
    font: { fileName: string; base64: string; id: string; type: string }
  ) {
    pdf.addFileToVFS(font.fileName, font.base64)
    pdf.addFont(font.fileName, font.id, font.type)
  }

  // Declarative list of the fonts shipped in dist/font/ and also hosted at
  // cdn.jsdelivr.net under the same names. To add a font: drop the TTF in
  // public/font/, add a row here, no other code changes needed.
  private static readonly DEFAULT_FONTS: ReadonlyArray<{
    fileName: string
    id: string
    type: 'normal' | 'bold' | 'italic' | 'bolditalic'
  }> = [
    { fileName: 'msyh.ttf', id: 'microsoft yahei', type: 'normal' },
    // Bold msyh shares the 'msyh.ttf' VFS name with the normal weight — that's
    // the existing behavior pre-0.4.0 (see git history). jsPDF keys fonts by
    // (id, type), so this works, but it means addFileToVFS overwrites once.
    { fileName: 'msyh-bold.ttf', id: 'microsoft yahei', type: 'bold' },
    { fileName: 'Arial.ttf', id: 'arial', type: 'normal' },
    { fileName: 'Arial_Bold.ttf', id: 'arial', type: 'bold' },
    { fileName: 'Arial_Italic.ttf', id: 'arial', type: 'italic' },
    { fileName: 'Arial_Bold_Italic.ttf', id: 'arial', type: 'bolditalic' },
    { fileName: 'calibri-regular.ttf', id: 'calibri', type: 'normal' },
    { fileName: 'calibri-bold.ttf', id: 'calibri', type: 'bold' },
    { fileName: 'calibri-italic.ttf', id: 'calibri', type: 'italic' },
    { fileName: 'calibri-bold-italic.ttf', id: 'calibri', type: 'bolditalic' },
    { fileName: 'Cambria.ttf', id: 'cambria', type: 'normal' },
    { fileName: 'cambriab.ttf', id: 'cambria', type: 'bold' },
    { fileName: 'cambriai.ttf', id: 'cambria', type: 'italic' },
    { fileName: 'cambriaz.ttf', id: 'cambria', type: 'bolditalic' },
    { fileName: 'Verdana.ttf', id: 'verdana', type: 'normal' },
    { fileName: 'Verdana_Bold.ttf', id: 'verdana', type: 'bold' },
    { fileName: 'Verdana_Italic.ttf', id: 'verdana', type: 'italic' },
    { fileName: 'Verdana_Bold_Italic.ttf', id: 'verdana', type: 'bolditalic' },
    { fileName: 'Inkfree.ttf', id: 'ink free', type: 'normal' },
    { fileName: 'segoe-ui.ttf', id: 'segoe ui', type: 'normal' },
    { fileName: 'segoe-ui-bold.ttf', id: 'segoe ui', type: 'bold' },
    { fileName: 'segoeuii.ttf', id: 'segoe ui', type: 'italic' }
  ]

  // Same version-pinned CDN base used pre-0.4.0. Pinning to 0.2.7 means the
  // fonts hosted there never change under us — bumping the package version
  // doesn't move the font URLs.
  private static readonly CDN_FONT_BASE =
    'https://cdn.jsdelivr.net/npm/canvas-editor-pdf@0.2.7/dist/font'

  private _resolveDefaultFontSource(fileName: string): string {
    if (this.fontSource === 'cdn') {
      return `${DrawPdf.CDN_FONT_BASE}/${fileName}`
    }
    if (this.fontSource === 'bundled') {
      return platform.getBundledFontPath(fileName)
    }
    // { dir }: consumer-supplied. Could be an absolute filesystem path (Node)
    // or a base URL (browser). loadFontAsBase64 in the relevant shim handles
    // both forms — Node validates path traversal, browser does the fetch.
    return `${this.fontSource.dir.replace(/\/+$/, '')}/${fileName}`
  }

  public async _addDefaultFont() {
    await Promise.all(
      DrawPdf.DEFAULT_FONTS.map(font =>
        this.downloadFont(
          this._resolveDefaultFontSource(font.fileName),
          font.fileName,
          font.id,
          font.type
        )
      )
    )

    return true
  }

  public loadDefaultFonts() {
    return (this.defaultFontsLoadedPromise = this._addDefaultFont())
  }

  public async addFont(
    url: string,
    fileName: string,
    id: string,
    type: string
  ) {
    await this.downloadFont(url, fileName, id, type)
    return true
  }

  public getFont(el: IElement, scale = 1): string {
    const { defaultSize, defaultFont } = this.options
    const font = el.font || defaultFont
    const size = (el.actualSize || el.size || defaultSize) * scale
    return `${el.italic ? 'italic ' : ''}${el.bold ? 'bold ' : ''}${size}px ${font}`
  }

  public getLetterReg(): RegExp {
    return this.LETTER_REG
  }

  public getPagePixelRatio(): number {
    return 1
  }

  public getMode(): EditorMode {
    return this.mode
  }

  // public isReadonly() {
  //   if (this.area.getActiveAreaInfo()?.area?.mode) {
  //     return this.area.isReadonly()
  //   }
  //   switch (this.mode) {
  //     case EditorMode.DESIGN:
  //       return false
  //     case EditorMode.READONLY:
  //     case EditorMode.PRINT:
  //     case EditorMode.GRAFFITI:
  //       return true
  //     case EditorMode.FORM:
  //       return !this.control.getIsRangeWithinControl()
  //     default:
  //       return false
  //   }
  // }

  public getArea(): Area {
    return this.area
  }

  public getBadge(): Badge {
    return this.badge
  }

  public filterAssistElement(elementList: IElement[]): IElement[] {
    return elementList.filter(element => {
      if (element.type === ElementType.TABLE) {
        const trList = element.trList!
        for (let r = 0; r < trList.length; r++) {
          const tr = trList[r]
          for (let d = 0; d < tr.tdList.length; d++) {
            const td = tr.tdList[d]
            td.value = this.filterAssistElement(td.value)
          }
        }
      }
      if (!element.controlId) return true
      // if (element.control?.minWidth) {
      if (
        element.controlComponent === ControlComponent.PREFIX ||
        element.controlComponent === ControlComponent.POSTFIX ||
        element.controlComponent === ControlComponent.PLACEHOLDER
      ) {
        element.value = ''
        return true
      }
      // }
      return true
      // return (
      //   element.controlComponent !== ControlComponent.PREFIX &&
      //   element.controlComponent !== ControlComponent.POSTFIX &&
      //   element.controlComponent !== ControlComponent.PLACEHOLDER
      // )
    })
  }

  public setMode(payload: EditorMode) {
    // if (this.mode === payload) return
    if (payload === EditorMode.PRINT) {
      this.printModeData = {
        header: this.header.getElementList(),
        main: this.elementList,
        footer: this.footer.getElementList()
      }
      const clonePrintModeData = deepClone(this.printModeData)
      const editorDataKeys: (keyof IEditorData)[] = ['header', 'main', 'footer']
      editorDataKeys.forEach(key => {
        clonePrintModeData[key] = this.filterAssistElement(
          clonePrintModeData[key]
        )
      })
      // this.setEditorData(clonePrintModeData)
    }
    // if (this.mode === EditorMode.PRINT && this.printModeData) {
    //   this.setEditorData(this.printModeData)
    //   this.printModeData = null
    // }
    this.clearSideEffect()
    this.mode = payload
    this.options.mode = payload
  }

  public isDesignMode() {
    return this.mode === EditorMode.DESIGN
  }

  public isPrintMode() {
    return this.mode === EditorMode.PRINT
  }

  public isGraffitiMode() {
    return this.mode === EditorMode.GRAFFITI
  }

  public isAreaHideDisabled() {
    return (
      this.isDesignMode() ||
      (this.isPrintMode() &&
        this.options.modeRule[EditorMode.PRINT].areaHideDisabled)
    )
  }

  public getIsPagingMode(): boolean {
    return this.options.pageMode === PageMode.PAGING
  }

  public getOriginalWidth(): number {
    const { paperDirection, width, height } = this.options
    return paperDirection === PaperDirection.VERTICAL ? width : height
  }

  public getOriginalHeight(): number {
    const { paperDirection, width, height } = this.options
    return paperDirection === PaperDirection.VERTICAL ? height : width
  }

  public getWidth(): number {
    return Math.floor(this.getOriginalWidth() * this.options.scale)
  }

  public getHeight(): number {
    return Math.floor(this.getOriginalHeight() * this.options.scale)
  }

  public getMainHeight(): number {
    const pageHeight = this.getHeight()
    return pageHeight - this.getMainOuterHeight()
  }

  public getMainOuterHeight(pageNo?: number): number {
    const margins = this.getMargins()
    const headerExtraHeight = this.header.getExtraHeight(pageNo)
    const footerExtraHeight = this.footer.getExtraHeight(pageNo)
    return margins[0] + margins[2] + headerExtraHeight + footerExtraHeight
  }

  public getCanvasWidth(pageNo = -1): number {
    // console.log('page')
    // console.log(pageNo)
    const page = this.ctxListInfos[pageNo] //this.getPdf().getPageInfo(pageNo + 1)
    // console.log(this.getCtx2d())
    // console.log(page)
    return page.width
  }

  public getCanvasHeight(pageNo = -1): number {
    const page = this.ctxListInfos[pageNo] //this.getPdf().getPageInfo(pageNo + 1)
    // const page = this.getPdf().getPageInfo(pageNo)
    return page.height
  }

  public getInnerWidth(): number {
    const width = this.getWidth()
    const margins = this.getMargins()
    return width - margins[1] - margins[3]
  }

  public getColumnLayout(): IColumnLayout | null {
    return this.columnManager.getLayout()
  }

  public setColumnConfig(config: IColumnOption | null): void {
    if (this.options.pageMode === PageMode.CONTINUITY) return
    this.columnManager.setConfig(config)
  }

  public getOriginalInnerWidth(): number {
    const width = this.getOriginalWidth()
    const margins = this.getOriginalMargins()
    return width - margins[1] - margins[3]
  }

  public getContextInnerWidth(): number {
    const positionContext = this.position.getPositionContext()
    if (positionContext.isTable) {
      const elementList = this.getOriginalElementList()
      const td = this.position.getTableTdByContext(elementList, positionContext)
      const tdPadding = this.getTdPadding()
      return td!.width! - tdPadding[1] - tdPadding[3]
    }
    // 分栏布局下按栏宽计算可用宽度（栏宽为缩放值，还原为未缩放单位）
    const columnLayout = this.getColumnLayout()
    if (columnLayout && columnLayout.count > 1) {
      return columnLayout.width / this.options.scale
    }
    return this.getOriginalInnerWidth()
  }

  public getMargins(): IMargin {
    return <IMargin>this.getOriginalMargins().map(m => m * this.options.scale)
  }

  public getOriginalMargins(): number[] {
    const { margins, paperDirection } = this.options
    return paperDirection === PaperDirection.VERTICAL
      ? margins
      : [margins[1], margins[2], margins[3], margins[0]]
  }

  public getPageGap(): number {
    return this.options.pageGap * this.options.scale
  }

  public getOriginalPageGap(): number {
    return this.options.pageGap
  }

  public getPageNumberBottom(): number {
    const {
      pageNumber: { bottom },
      scale
    } = this.options
    return bottom * scale
  }

  public getMarginIndicatorSize(): number {
    return this.options.marginIndicatorSize * this.options.scale
  }

  public getDefaultBasicRowMarginHeight(): number {
    return this.options.defaultBasicRowMarginHeight * this.options.scale
  }

  public getHighlightMarginHeight(): number {
    return this.options.highlightMarginHeight * this.options.scale
  }

  public getTdPadding(): IPadding {
    const {
      table: { tdPadding },
      scale
    } = this.options
    return <IPadding>tdPadding.map(m => m * scale)
  }

  // public getContainer(): HTMLDivElement {
  //   return this.container
  // }

  public getPageNo(): number {
    return this.pageNo
  }

  public setPageNo(payload: number) {
    this.pageNo = payload
  }

  public getPage(pageNo = -1): HTMLCanvasElement {
    return this.pageList[~pageNo ? pageNo : this.pageNo]
  }

  public getPageList(): HTMLCanvasElement[] {
    return this.pageList
  }

  public getPageCount(): number {
    return this.pageList.length
  }

  public getTableRowList(sourceElementList: IElement[]): IRow[] {
    const positionContext = this.position.getPositionContext()
    return this.position.getTableTdByContext(
      sourceElementList,
      positionContext
    )!.rowList!
  }

  public getOriginalRowList() {
    // const zoneManager = this.getZone()
    // if (zoneManager.isHeaderActive()) {
    //   return this.header.getRowList()
    // }
    // if (zoneManager.isFooterActive()) {
    //   return this.footer.getRowList()
    // }
    return this.rowList
  }

  public getRowList(): IRow[] {
    const positionContext = this.position.getPositionContext()
    return positionContext.isTable
      ? this.getTableRowList(this.getOriginalElementList())
      : this.getOriginalRowList()
  }

  public getPageRowList(): IRow[][] {
    return this.pageRowList
  }

  public getPdf(): jsPDF {
    return this.pdf
  }

  public getOptions(): DeepRequired<IEditorOption> {
    return this.options
  }

  // Merge new options into the current ones at runtime. Kept as public API
  // (documented) even though the PDF-only fork usually configures via the
  // constructor; call render() afterwards to reflect changes.
  public updateOptions(payload: IUpdateOption | object) {
    const newOption = mergeOption(payload as IEditorOption)
    Object.entries(newOption).forEach(([key, value]) => {
      Reflect.set(this.options, key, value)
    })
    this.setColumnConfig(this.options.column)
  }

  public getGroup(): Group {
    return this.group
  }

  public getPosition(): Position {
    return this.position
  }

  public getColumnManager(): ColumnManager {
    return this.columnManager
  }

  public getLineBreakParticle(): LineBreakParticle {
    return this.lineBreakParticle
  }

  public getTextParticle(): TextParticle {
    return this.textParticle
  }

  public getHeaderElementList(): IElement[] {
    return this.header.getElementList()
  }

  public getTableElementList(sourceElementList: IElement[]): IElement[] {
    const positionContext = this.position.getPositionContext()
    return (
      this.position.getTableTdByContext(sourceElementList, positionContext)
        ?.value || []
    )
  }

  public getElementList(): IElement[] {
    const positionContext = this.position.getPositionContext()
    const elementList = this.getOriginalElementList()
    return positionContext.isTable
      ? this.getTableElementList(elementList)
      : elementList
  }

  public getMainElementList(): IElement[] {
    const positionContext = this.position.getPositionContext()
    return positionContext.isTable
      ? this.getTableElementList(this.elementList)
      : this.elementList
  }

  public getOriginalElementList() {
    // const zoneManager = this.getZone()
    // if (zoneManager.isHeaderActive()) {
    //   return this.getHeaderElementList()
    // }
    // if (zoneManager.isFooterActive()) {
    //   return this.getFooterElementList()
    // }
    return this.elementList
  }

  public getOriginalMainElementList(): IElement[] {
    return this.elementList
  }

  public getFooterElementList(): IElement[] {
    return this.footer.getElementList()
  }

  public getTd(): ITd | null {
    const positionContext = this.position.getPositionContext()
    if (!positionContext.isTable) return null
    return this.position.getTableTdByContext(
      this.getOriginalElementList(),
      positionContext
    )
  }

  public spliceElementList(
    elementList: IElement[],
    start: number,
    deleteCount: number,
    ...items: IElement[]
  ) {
    // const isDesignMode = this.isDesignMode()
    if (deleteCount > 0) {
      // 当最后元素与开始元素列表信息不一致时：清除当前列表信息
      const endIndex = start + deleteCount
      const endElement = elementList[endIndex]
      const endElementListId = endElement?.listId
      if (
        endElementListId &&
        elementList[start - 1]?.listId !== endElementListId
      ) {
        let startIndex = endIndex
        while (startIndex < elementList.length) {
          const curElement = elementList[startIndex]
          if (
            curElement.listId !== endElementListId ||
            curElement.value === ZERO
          ) {
            break
          }
          delete curElement.listId
          delete curElement.listType
          delete curElement.listStyle
          startIndex++
        }
      }
      // // 元素删除（不可删除控件忽略）
      // if (
      //   !isIgnoreDeletedRule &&
      //   !this.isDesignMode() &&
      //   !this.control.getIsRangeWithinControl()
      // ) {
      //   const tdDeletable = this.getTd()?.deletable
      //   let deleteIndex = endIndex - 1
      //   while (deleteIndex >= start) {
      //     const deleteElement = elementList[deleteIndex]
      //     if (
      //       deleteElement?.hide ||
      //       deleteElement?.control?.hide ||
      //       deleteElement?.area?.hide ||
      //       (tdDeletable !== false &&
      //         deleteElement?.control?.deletable !== false &&
      //         (!deleteElement.controlId ||
      //           this.mode !== EditorMode.FORM ||
      //           !modeRule[this.mode].controlDeletableDisabled) &&
      //         deleteElement?.title?.deletable !== false &&
      //         (group.deletable !== false || !deleteElement.groupIds?.length) &&
      //         (deleteElement?.area?.deletable !== false ||
      //           deleteElement?.areaIndex !== 0))
      //     ) {
      //       elementList.splice(deleteIndex, 1)
      //     }
      //     deleteIndex--
      //   }
      // } else {
      //   elementList.splice(start, deleteCount)
      // }
    }
    // 循环添加，避免使用解构影响性能
    if (items?.length) {
      for (let i = 0; i < items.length; i++) {
        elementList.splice(start + i, 0, items[i])
      }
    }
  }

  public getImageParticle(): ImageParticle {
    return this.imageParticle
  }

  public getTableParticle(): TableParticle {
    return this.tableParticle
  }

  public getHeader(): Header {
    return this.header
  }

  public getFooter(): Footer {
    return this.footer
  }

  public getHyperlinkParticle(): HyperlinkParticle {
    return this.hyperlinkParticle
  }

  public getDateParticle(): DateParticle {
    return this.dateParticle
  }

  public getListParticle(): ListParticle {
    return this.listParticle
  }

  public getCheckboxParticle(): CheckboxParticle {
    return this.checkboxParticle
  }

  public getRadioParticle(): RadioParticle {
    return this.radioParticle
  }

  // public getControl(): Control {
  //   return this.control
  // }

  public getImageObserver(): ImageObserver {
    return this.imageObserver
  }

  public getI18n(): I18n {
    return this.i18n
  }

  public getGraffiti(): Graffiti {
    return this.graffiti
  }

  public async setValue(payload: Partial<IEditorData>) {
    const { header, main, footer } = deepClone(payload)
    if (!header && !main && !footer) return
    const pageComponentData = [header, main, footer]
    for (let i = 0; i < pageComponentData.length; i++) {
      const data = pageComponentData[i]
      // Skip an absent zone — `return` here aborted the whole method (and
      // never reached setEditorData) whenever header/footer were missing.
      if (!data) continue
      await formatElementList(data, {
        editorOptions: this.options,
        isForceCompensation: true
      })
    }
    pageComponentData.forEach(datas => {
      if (datas) {
        datas = this.filterAssistElement(datas)
      }
    })
    this.setEditorData({
      header,
      main,
      footer
    })
  }

  public getOriginValue(
    options: IGetOriginValueOption = {}
  ): Required<IEditorData> {
    const { pageNo } = options
    let mainElementList = this.elementList
    if (
      Number.isInteger(pageNo) &&
      pageNo! >= 0 &&
      pageNo! < this.pageRowList.length
    ) {
      mainElementList = this.pageRowList[pageNo!].flatMap(
        row => row.elementList
      )
    }
    const data: Required<IEditorData> = {
      header: this.getHeaderElementList(),
      main: mainElementList,
      footer: this.getFooterElementList(),
      graffiti: this.graffiti.getValue()
    }
    return data
  }

  public getValue(options: IGetValueOption = {}): IEditorData {
    const originData = this.getOriginValue({ pageNo: options.pageNo })
    const { extraPickAttrs } = options
    const data: IEditorData = {
      header: zipElementList(originData.header, {
        extraPickAttrs
      }),
      main: zipElementList(originData.main, {
        extraPickAttrs,
        isClassifyArea: true
      }),
      footer: zipElementList(originData.footer, {
        extraPickAttrs
      }),
      graffiti: originData.graffiti
    }
    return data
  }

  public setEditorData(payload: Partial<Omit<IEditorData, 'graffiti'>>) {
    const { header, main, footer } = payload
    if (header) {
      this.header.setElementList(header)
    }
    if (main) {
      this.elementList = main
    }
    if (footer) {
      this.footer.setElementList(footer)
    }
  }

  private _formatContainer() {
    // 容器宽度需跟随纸张宽度
    // this.container.style.position = 'relative'
    // this.container.style.width = `${this.getWidth()}px`
    // this.container.setAttribute(EDITOR_COMPONENT, EditorComponent.MAIN)
  }

  private _createPage(pageNo: number) {
    const width = this.getWidth()
    const height = this.getHeight()
    // // 调整分辨率
    const dpr = 1 //this.getPagePixelRatio()

    const orientation =
      this.getOptions().paperDirection === PaperDirection.VERTICAL ? 'p' : 'l'
    this.ctxListInfos.push({
      width,
      height,
      background: '#ffffff',
      marginBottom: `${this.getPageGap()}px`,
      index: String(pageNo)
    })
    this.pageList.push({
      width,
      height,
      background: '#ffffff',
      marginBottom: `${this.getPageGap()}px`,
      index: String(pageNo)
    })
    const newPagePdf = this.pdf.addPage([width, height], orientation)
    newPagePdf.context2d.scale(dpr, dpr)
  }

  public getElementSize(el: IElement) {
    return el.actualSize || el.size || this.options.defaultSize
  }

  public getElementRowMargin(el: IElement) {
    const {
      defaultSize,
      defaultBasicRowMarginHeight,
      defaultRowMargin,
      scale
    } = this.options
    // 字体在12-30之间，行间距不变，小于12按比例缩小，大于30按比例放大
    const fontSize = el.size || defaultSize
    let ratio = 1
    if (fontSize < 12) {
      ratio = fontSize / 12
    } else if (fontSize > 30) {
      ratio = 1 + (fontSize - 30) / 30
    }
    return (
      defaultBasicRowMarginHeight *
      ratio *
      (el.rowMargin ?? defaultRowMargin) *
      scale
    )
  }

  public computeRowList(payload: IComputeRowListPayload) {
    const {
      innerWidth,
      elementList,
      isPagingMode = false,
      isFromTable = false,
      startX = 0,
      startY = 0,
      pageHeight = 0,
      surroundElementList = []
    } = payload
    const {
      defaultSize,
      scale,
      imgCaption,
      table: { tdPadding, defaultColMinWidth, overflow },
      defaultTabWidth
    } = this.options
    const defaultBasicRowMarginHeight = this.getDefaultBasicRowMarginHeight()
    // 还原最小宽度控件占位
    if (this.controlMinWidthPlaceholderElementListSet.has(elementList)) {
      for (let i = elementList.length - 1; i >= 0; i--) {
        if (elementList[i].isControlMinWidthPlaceholder) {
          elementList.splice(i, 1)
        }
      }
      this.controlMinWidthPlaceholderElementListSet.delete(elementList)
    }
    // 计算列表偏移宽度
    const listStyleMap = this.listParticle.computeListStyle(
      this.getCtx2d(),
      elementList
    )
    const rowList: IRow[] = []
    const layout =
      isPagingMode && !isFromTable ? this.columnManager.getLayout() : null
    const isColumnEnabled = !!layout && layout.count > 1
    if (elementList.length) {
      rowList.push({
        width: 0,
        height: 0,
        ascent: 0,
        elementList: [],
        startIndex: 0,
        rowIndex: 0,
        rowFlex: elementList?.[0]?.rowFlex || elementList?.[1]?.rowFlex,
        ...(isColumnEnabled ? { columnIndex: 0 } : {})
      })
    }
    // 起始位置及页码计算
    let x = startX
    let y = startY
    let pageNo = 0
    // 分页模式下按页计算起始 Y（页眉/页脚禁用时该页起始位置上移）
    let pageStartY = startY
    if (isPagingMode && !isFromTable) {
      pageStartY = this.getMargins()[0] + this.getHeader().getExtraHeight(0)
      y = pageStartY
    }
    // 列表位置
    // 不同 listId 独立计数，避免父列表与子列表序号互相影响
    const listIndexMap: Map<string, number> = new Map()
    // 控件最小宽度
    let controlRealWidth = 0
    // 分栏游标
    let currentColumn = 0
    for (let i = 0; i < elementList.length; i++) {
      const curRow: IRow = rowList[rowList.length - 1]
      const element = elementList[i]
      const rowMargin = this.getElementRowMargin(element)
      const metrics: IElementMetrics = {
        width: 0,
        height: 0,
        boundingBoxAscent: 0,
        boundingBoxDescent: 0
      }
      // 实际可用宽度
      const offsetX =
        curRow.offsetX ||
        (element.listId &&
          (listStyleMap.get(element.listId) || 0) +
            (element.listLevel
              ? this.listParticle.LIST_INDENT_WIDTH * element.listLevel * scale
              : 0)) ||
        0
      const rowMaxWidth = isColumnEnabled && layout ? layout.width : innerWidth
      const availableWidth = rowMaxWidth - offsetX
      // 增加起始位置坐标偏移量
      const isStartElement = curRow.elementList.length === 1
      x += isStartElement ? offsetX : 0
      y += isStartElement ? curRow.offsetY || 0 : 0
      if (
        (element.hide || element.control?.hide || element.area?.hide) &&
        !this.isDesignMode()
      ) {
        const preElement = curRow.elementList[curRow.elementList.length - 1]
        metrics.height =
          preElement?.metrics.height || this.options.defaultSize * scale
        metrics.boundingBoxAscent = preElement?.metrics.boundingBoxAscent || 0
        metrics.boundingBoxDescent = preElement?.metrics.boundingBoxDescent || 0
      } else if (
        element.type === ElementType.IMAGE ||
        element.type === ElementType.LATEX
      ) {
        // 浮动图片无需计算数据
        if (
          element.imgDisplay === ImageDisplay.SURROUND ||
          element.imgDisplay === ImageDisplay.FLOAT_TOP ||
          element.imgDisplay === ImageDisplay.FLOAT_BOTTOM
        ) {
          metrics.width = 0
          metrics.height = 0
          metrics.boundingBoxDescent = 0
        } else {
          const elementWidth = element.width! * scale
          const elementHeight = element.height! * scale
          // 图片超出尺寸后自适应（图片大小大于可用宽度时）
          if (elementWidth > availableWidth) {
            const adaptiveHeight =
              (elementHeight * availableWidth) / elementWidth
            element.width = availableWidth / scale
            element.height = adaptiveHeight / scale
            metrics.width = availableWidth
            metrics.height = adaptiveHeight
            metrics.boundingBoxDescent = adaptiveHeight
          } else {
            metrics.width = elementWidth
            metrics.height = elementHeight
            metrics.boundingBoxDescent = elementHeight
          }
          // 增加题注高度
          if (element.imgCaption?.value) {
            const fontSize = element.imgCaption.size || imgCaption.size
            const captionTop = element.imgCaption.top ?? imgCaption.top
            const captionHeight = (fontSize + captionTop) * scale
            metrics.boundingBoxAscent += captionHeight
          }
        }
      } else if (element.type === ElementType.TABLE) {
        const tdPaddingWidth = tdPadding[1] + tdPadding[3]
        const tdPaddingHeight = tdPadding[0] + tdPadding[2]
        // 表格跨页在渲染层拆分行（数据层保持单一表格）
        const trList = element.trList!
        // 重置tr高度：行高不可低于一个单元格最小高度
        const tdMinHeight =
          tdPaddingHeight + defaultSize + (rowMargin * 2) / scale
        for (let t = 0; t < trList.length; t++) {
          const tr = trList[t]
          // 行高默认当前最小高度，后续根据内容自适应
          tr.height = Math.max(tdMinHeight, tr.minHeight || 0)
          tr.minHeight = tr.height
        }
        // 表格不允许超出正文区域时：等比例压缩列宽至内容区内，并清除横向偏移
        if (!overflow) {
          shrinkColgroupToWidth(
            element.colgroup!,
            this.getOriginalInnerWidth(),
            defaultColMinWidth
          )
          element.translateX = 0
        }
        // 计算表格行列
        this.tableParticle.computeRowColInfo(element)
        // 计算表格内元素信息
        for (let t = 0; t < trList.length; t++) {
          const tr = trList[t]
          for (let d = 0; d < tr.tdList.length; d++) {
            const td = tr.tdList[d]
            const rowList = this.computeRowList({
              innerWidth: (td.width! - tdPaddingWidth) * scale,
              elementList: td.value,
              isFromTable: true,
              isPagingMode
            })
            const rowHeight = rowList.reduce((pre, cur) => pre + cur.height, 0)
            td.rowList = rowList
            // 移除缩放导致的行高变化-渲染时会进行缩放调整
            const curTdHeight = rowHeight / scale + tdPaddingHeight
            // 内容高度大于当前单元格高度需增加
            if (td.height! < curTdHeight) {
              const extraHeight = curTdHeight - td.height!
              const changeTr = trList[t + td.rowspan - 1]
              changeTr.height += extraHeight
              changeTr.tdList.forEach(changeTd => {
                changeTd.height! += extraHeight
                if (!changeTd.realHeight) {
                  changeTd.realHeight = changeTd.height!
                } else {
                  changeTd.realHeight! += extraHeight
                }
              })
            }
            // 当前单元格最小高度及真实高度（包含跨列）
            let curTdMinHeight = 0
            let curTdRealHeight = 0
            let i = 0
            while (i < td.rowspan) {
              const curTr = trList[i + t] || trList[t]
              curTdMinHeight += curTr.minHeight!
              curTdRealHeight += curTr.height!
              i++
            }
            td.realMinHeight = curTdMinHeight
            td.realHeight = curTdRealHeight
            td.mainHeight = curTdHeight
          }
        }
        // 单元格高度大于实际内容高度需减少
        const reduceTrList = this.tableParticle.getTrListGroupByCol(trList)
        for (let t = 0; t < reduceTrList.length; t++) {
          const tr = reduceTrList[t]
          let reduceHeight = -1
          for (let d = 0; d < tr.tdList.length; d++) {
            const td = tr.tdList[d]
            const curTdRealHeight = td.realHeight!
            const curTdHeight = td.mainHeight!
            const curTdMinHeight = td.realMinHeight!
            // 获取最大可减少高度
            const curReduceHeight =
              curTdHeight < curTdMinHeight
                ? curTdRealHeight - curTdMinHeight
                : curTdRealHeight - curTdHeight
            if (!~reduceHeight || curReduceHeight < reduceHeight) {
              reduceHeight = curReduceHeight
            }
          }
          if (reduceHeight > 0) {
            const changeTr = trList[t]
            changeTr.height -= reduceHeight
            changeTr.tdList.forEach(changeTd => {
              changeTd.height! -= reduceHeight
              changeTd.realHeight! -= reduceHeight
            })
          }
        }
        // 需要重新计算表格内值
        this.tableParticle.computeRowColInfo(element)
        // 计算出表格高度
        const tableHeight = this.tableParticle.getTableHeight(element)
        const tableWidth = this.tableParticle.getTableWidth(element)
        element.width = tableWidth
        element.height = tableHeight
        const elementWidth = tableWidth * scale
        const elementHeight = tableHeight * scale
        metrics.width = elementWidth
        metrics.height = elementHeight
        metrics.boundingBoxDescent = elementHeight
        metrics.boundingBoxAscent = -rowMargin
        // 后一个元素也是表格则移除行间距
        if (elementList[i + 1]?.type === ElementType.TABLE) {
          metrics.boundingBoxAscent -= rowMargin
        }
      } else if (element.type === ElementType.SEPARATOR) {
        const {
          separator: { lineWidth: defaultLineWidth }
        } = this.options
        const lineWidth = element.lineWidth || defaultLineWidth
        element.width = availableWidth / scale
        metrics.width = availableWidth
        metrics.height = lineWidth * scale
        metrics.boundingBoxAscent = -rowMargin
        metrics.boundingBoxDescent = -rowMargin + metrics.height
      } else if (element.type === ElementType.PAGE_BREAK) {
        element.width = availableWidth / scale
        metrics.width = availableWidth
        metrics.height = defaultSize
      } else if (
        element.type === ElementType.RADIO ||
        element.controlComponent === ControlComponent.RADIO
      ) {
        const { width, height, gap } = this.options.radio
        const elementWidth = width + gap * 2
        element.width = elementWidth
        metrics.width = elementWidth * scale
        metrics.height = height * scale
      } else if (
        element.type === ElementType.CHECKBOX ||
        element.controlComponent === ControlComponent.CHECKBOX
      ) {
        const { width, height, gap } = this.options.checkbox
        const elementWidth = width + gap * 2
        element.width = elementWidth
        metrics.width = elementWidth * scale
        metrics.height = height * scale
      } else if (element.type === ElementType.TAB) {
        metrics.width = defaultTabWidth * scale
        metrics.height = defaultSize * scale
        metrics.boundingBoxDescent = 0
        metrics.boundingBoxAscent =
          this.textParticle.getBasisWordBoundingBoxAscent(
            this.getCtx2d(),
            this.getCtx2d().font
          )
      } else if (element.isControlMinWidthPlaceholder) {
        metrics.width = (element.width || 0) * scale
        metrics.height = defaultSize * scale
        this.getCtx2d().font = this.getFont(element)
        const basisMetrics = this.textParticle.measureBasisWord(
          this.getCtx2d(),
          element.font!
        )
        metrics.boundingBoxAscent = basisMetrics.actualBoundingBoxAscent * scale
        metrics.boundingBoxDescent =
          basisMetrics.actualBoundingBoxDescent * scale
      } else if (element.type === ElementType.BLOCK) {
        if (!element.width) {
          metrics.width = availableWidth
        } else {
          const elementWidth = element.width * scale
          metrics.width = Math.min(elementWidth, availableWidth)
        }
        metrics.height = element.height! * scale
        metrics.boundingBoxDescent = metrics.height
        metrics.boundingBoxAscent = 0
      } else if (element.type === ElementType.LABEL) {
        const {
          defaultSize,
          label: { defaultPadding }
        } = this.options
        this.getCtx2d().font = this.getFont(element)
        const fontMetrics = this.textParticle.measureText(
          this.getCtx2d(),
          element
        )
        metrics.width =
          (fontMetrics.width + defaultPadding[1] + defaultPadding[3]) * scale
        metrics.height = (element.size || defaultSize) * scale
        metrics.boundingBoxDescent = 0
        metrics.boundingBoxAscent =
          (defaultPadding[0] + fontMetrics.actualBoundingBoxAscent) * scale
      } else {
        // 设置上下标真实字体尺寸
        const size = element.size || defaultSize
        if (
          element.type === ElementType.SUPERSCRIPT ||
          element.type === ElementType.SUBSCRIPT
        ) {
          element.actualSize = Math.ceil(size * 0.6)
        }
        metrics.height = (element.actualSize || size) * scale
        this.getCtx2d().font = this.getFont(element)
        const fontMetrics = this.textParticle.measureText(
          this.getCtx2d(),
          element
        )
        metrics.width = fontMetrics.width * scale
        if (element.letterSpacing) {
          metrics.width += element.letterSpacing * scale
        }
        // 零宽字符ascent默认为：基线元素ascent
        metrics.boundingBoxAscent =
          (element.value === ZERO
            ? this.textParticle.getBasisWordBoundingBoxAscent(
                this.getCtx2d(),
                element.font!
              )
            : fontMetrics.actualBoundingBoxAscent) * scale
        metrics.boundingBoxDescent =
          fontMetrics.actualBoundingBoxDescent * scale
        if (element.type === ElementType.SUPERSCRIPT) {
          metrics.boundingBoxAscent += metrics.height / 2
        } else if (element.type === ElementType.SUBSCRIPT) {
          metrics.boundingBoxDescent += metrics.height / 2
        }
      }
      const ascent =
        !element.hide &&
        ((element.imgDisplay !== ImageDisplay.INLINE &&
          element.type === ElementType.IMAGE) ||
          element.type === ElementType.LATEX)
          ? metrics.height + rowMargin
          : metrics.boundingBoxAscent + rowMargin
      const height =
        rowMargin +
        metrics.boundingBoxAscent +
        metrics.boundingBoxDescent +
        rowMargin
      const rowElement: IRowElement = Object.assign(element, {
        metrics,
        left: 0,
        style: this.getFont(element, scale)
      })
      // 控件开始时统计宽度，结束时消费最小宽度并补充跨行占位
      if (
        rowElement.control?.minWidth &&
        !rowElement.isControlMinWidthPlaceholder
      ) {
        if (rowElement.controlComponent) {
          controlRealWidth += metrics.width
        }
        if (rowElement.controlComponent === ControlComponent.POSTFIX) {
          const controlMinWidth = rowElement.control.minWidth * scale
          const extraWidth = controlMinWidth - controlRealWidth
          const rowRemainingWidth = Math.max(
            availableWidth - curRow.width - rowElement.metrics.width,
            0
          )
          // 消费超出实际最小宽度的长度
          if (extraWidth > 0) {
            // 超出行宽时截断
            const left = Math.min(rowRemainingWidth, extraWidth) * scale
            rowElement.left = left
            curRow.width += left
          }
          let placeholderWidth = extraWidth - rowRemainingWidth
          const placeholderList: IElement[] = []
          while (placeholderWidth > 0) {
            const width = Math.min(placeholderWidth, availableWidth)
            placeholderList.push({
              ...rowElement,
              value: '',
              width: width / scale,
              left: 0,
              isControlMinWidthPlaceholder: true
            } as IElement)
            placeholderWidth -= width
          }
          if (placeholderList.length) {
            elementList.splice(i + 1, 0, ...placeholderList)
            this.controlMinWidthPlaceholderElementListSet.add(elementList)
          }
          controlRealWidth = 0
        }
      }
      // 超过限定宽度
      const preElement = elementList[i - 1]
      let nextElement = elementList[i + 1]
      // 累计行宽 + 当前元素宽度 + 排版宽度(英文单词整体宽度 + 后面标点符号宽度)
      let curRowWidth = curRow.width + metrics.width
      if (this.options.wordBreak === WordBreak.BREAK_WORD) {
        if (
          (!preElement?.type || preElement?.type === ElementType.TEXT) &&
          (!element.type || element.type === ElementType.TEXT)
        ) {
          // 英文单词
          const word = `${preElement?.value || ''}${element.value}`
          if (this.WORD_LIKE_REG.test(word)) {
            const { width, endElement } = this.textParticle.measureWord(
              this.getCtx2d(),
              elementList,
              i
            )
            // 后面存在元素 && 单词宽度大于行可用宽度，无需折行
            const wordWidth = width * scale
            if (endElement && wordWidth <= availableWidth) {
              curRowWidth += wordWidth
              nextElement = endElement
            }
          }
          // 标点符号
          const punctuationWidth = this.textParticle.measurePunctuationWidth(
            this.getCtx2d(),
            nextElement
          )
          curRowWidth += punctuationWidth * scale
        }
      }
      // 列表信息
      if (element.listId && element.value === ZERO && !element.listWrap) {
        if (listIndexMap.has(element.listId)) {
          listIndexMap.set(
            element.listId,
            (listIndexMap.get(element.listId) ?? 0) + 1
          )
        } else {
          listIndexMap.set(element.listId, 0)
        }
      }
      // 计算四周环绕导致的元素偏移量
      const surroundPosition = this.position.setSurroundPosition({
        pageNo,
        rowElement,
        row: curRow,
        rowElementRect: {
          x,
          y,
          height,
          width: metrics.width
        },
        availableWidth,
        surroundElementList
      })
      x = surroundPosition.x
      curRowWidth += surroundPosition.rowIncreaseWidth
      x += metrics.width
      // 是否强制换行
      const isForceBreak =
        element.type === ElementType.SEPARATOR ||
        element.type === ElementType.TABLE ||
        preElement?.type === ElementType.TABLE ||
        preElement?.type === ElementType.BLOCK ||
        element.type === ElementType.BLOCK ||
        preElement?.imgDisplay === ImageDisplay.INLINE ||
        element.imgDisplay === ImageDisplay.INLINE ||
        preElement?.listId !== element.listId ||
        (preElement?.areaId !== element.areaId && !element.area?.hide) ||
        (i !== 0 && element.value === ZERO && !element.area?.hide)
      // 是否宽度不足导致换行
      const isWidthNotEnough = curRowWidth > availableWidth
      const isWrap = isForceBreak || isWidthNotEnough
      // 新行数据处理
      if (isWrap) {
        const row: IRow = {
          width: metrics.width,
          height,
          startIndex: i,
          elementList: [rowElement],
          ascent,
          rowIndex: curRow.rowIndex + 1,
          rowFlex: elementList[i]?.rowFlex || elementList[i + 1]?.rowFlex,
          isPageBreak: element.type === ElementType.PAGE_BREAK,
          ...(isColumnEnabled ? { columnIndex: currentColumn } : {})
        }
        // 控件缩进
        if (
          rowElement.controlComponent !== ControlComponent.PREFIX &&
          rowElement.control?.indentation === ControlIndentation.VALUE_START
        ) {
          // 查找到非前缀的第一个元素位置
          const preStartIndex = curRow.elementList.findIndex(
            el =>
              el.controlId === rowElement.controlId &&
              el.controlComponent !== ControlComponent.PREFIX
          )
          if (~preStartIndex) {
            const preRowPositionList = this.position.computeRowPosition({
              row: curRow,
              innerWidth: this.getInnerWidth()
            })
            const valueStartPosition = preRowPositionList[preStartIndex]
            if (valueStartPosition) {
              row.offsetX = valueStartPosition.coordinate.leftTop[0]
            }
          }
        }
        // 列表缩进
        if (element.listId) {
          row.isList = true
          row.offsetX =
            (listStyleMap.get(element.listId!) || 0) +
            (element.listLevel
              ? this.listParticle.LIST_INDENT_WIDTH * element.listLevel * scale
              : 0)
          row.listIndex = listIndexMap.get(element.listId!) ?? 0
        }
        // Y轴偏移量
        row.offsetY =
          !isFromTable &&
          element.area?.top &&
          element.areaId !== elementList[i - 1]?.areaId
            ? element.area.top * scale
            : 0
        rowList.push(row)
      } else {
        curRow.width += metrics.width
        // 减小块元素前第一行空行行高
        if (
          i === 0 &&
          (getIsBlockElement(elementList[1]) || !!elementList[1]?.areaId)
        ) {
          curRow.height = defaultBasicRowMarginHeight
          curRow.ascent = defaultBasicRowMarginHeight
        } else if (curRow.height < height) {
          curRow.height = height
          curRow.ascent = ascent
        }
        curRow.elementList.push(rowElement)
      }
      // 行结束时逻辑
      if (isWrap || i === elementList.length - 1) {
        // 行内全部为隐藏元素时 => 行高折叠
        if (!this.isDesignMode() && curRow.height > 0) {
          // Only collapse a row whose (non-zero-width) content is entirely
          // hidden. Guard against an empty result: a newline-only row filters
          // down to [], and [].every() is true — collapsing it would wrongly
          // remove intentional blank lines and shift pagination.
          const visibleElements = curRow.elementList.filter(
            el => el.value !== ZERO
          )
          const isAllHidden =
            visibleElements.length > 0 &&
            visibleElements.every(
              el => el.hide || el.control?.hide || el.area?.hide
            )
          if (isAllHidden) {
            curRow.height = 0
            curRow.ascent = 0
          }
        }
        // 换行原因：宽度不足
        curRow.isWidthNotEnough = isWidthNotEnough && !isForceBreak
        // 两端对齐、分散对齐
        if (
          !curRow.isSurround &&
          (preElement?.rowFlex === RowFlex.JUSTIFY ||
            (preElement?.rowFlex === RowFlex.ALIGNMENT &&
              curRow.isWidthNotEnough))
        ) {
          // 忽略换行符及尾部元素间隔设置
          const rowElementList =
            curRow.elementList[0]?.value === ZERO
              ? curRow.elementList.slice(1)
              : curRow.elementList
          const gap =
            (availableWidth - curRow.width) / (rowElementList.length - 1)
          for (let e = 0; e < rowElementList.length - 1; e++) {
            const el = rowElementList[e]
            el.metrics.width += gap
          }
          curRow.width = availableWidth
        }
      }
      // 重新计算坐标、页码、下一行首行元素环绕交叉
      if (isWrap) {
        const columnOffset = !layout ? 0 : layout.offsets[currentColumn] || 0
        x = startX + columnOffset
        y += curRow.height
        if (isPagingMode && !isFromTable && pageHeight) {
          const curMainOuterHeight = this.getMainOuterHeight(pageNo)
          const isOverflow =
            y - pageStartY + curMainOuterHeight + height > pageHeight
          const isPageBreakElement = element.type === ElementType.PAGE_BREAK
          if (isOverflow || isPageBreakElement) {
            if (
              !isPageBreakElement &&
              isColumnEnabled &&
              layout &&
              currentColumn < layout.count - 1
            ) {
              currentColumn += 1
              y = pageStartY
              x = startX + (layout.offsets[currentColumn] || 0)
            } else {
              // 删除多余四周环绕型元素
              deleteSurroundElementList(surroundElementList, pageNo)
              pageNo += 1
              currentColumn = 0
              pageStartY =
                this.getMargins()[0] + this.getHeader().getExtraHeight(pageNo)
              y = pageStartY
              x = startX + (layout ? layout.offsets[0] || 0 : 0)
            }
          }
        }
        // 同步新行的栏索引（栏游标可能在翻栏/翻页逻辑中变化）
        const nextRow = rowList[rowList.length - 1]
        if (nextRow && isColumnEnabled && nextRow.columnIndex !== undefined) {
          nextRow.columnIndex = currentColumn
        }
        // 计算下一行第一个元素是否存在环绕交叉
        rowElement.left = 0
        const surroundPosition = this.position.setSurroundPosition({
          pageNo,
          rowElement,
          row: nextRow,
          rowElementRect: {
            x,
            y,
            height,
            width: metrics.width
          },
          availableWidth,
          surroundElementList
        })
        x = surroundPosition.x
        x += metrics.width
      }
    }
    return rowList
  }

  private _computePageList(): IRow[][] {
    const pageRowList: IRow[][] = [[]]
    const {
      pageMode,
      pageNumber: { maxPageNo }
    } = this.options
    const height = this.getHeight()
    let pageNo = 0
    if (pageMode === PageMode.CONTINUITY) {
      const marginHeight = this.getMainOuterHeight(0)
      let pageHeight = marginHeight
      pageRowList[0] = this.rowList
      // 重置高度
      pageHeight += this.rowList.reduce(
        (pre, cur) => pre + cur.height + (cur.offsetY || 0),
        0
      )
      const dpr = this.getPagePixelRatio()
      const pageDom = this.pageList[0]
      const pageDomHeight = Number(pageDom.style.height.replace('px', ''))
      if (pageHeight > pageDomHeight) {
        pageDom.style.height = `${pageHeight}px`
        pageDom.height = pageHeight * dpr
      } else {
        const reduceHeight = pageHeight < height ? height : pageHeight
        pageDom.style.height = `${reduceHeight}px`
        pageDom.height = reduceHeight * dpr
      }
    } else {
      // 每页页眉/页脚禁用状态可能不同，按页计算外部占位高度
      let pageHeight = this.getMainOuterHeight(0)
      let prevColumnIndex: number | undefined = undefined
      for (let i = 0; i < this.rowList.length; i++) {
        const row = this.rowList[i]
        const rowOffsetY = row.offsetY || 0
        // 分栏内栏切换：重置当前页累计高度，留在本页
        const columnChanged =
          prevColumnIndex !== undefined &&
          row.columnIndex !== undefined &&
          row.columnIndex > 0 &&
          row.columnIndex !== prevColumnIndex
        if (columnChanged) {
          pageHeight = this.getMainOuterHeight(pageNo) + row.height + rowOffsetY
          pageRowList[pageNo].push(row)
        } else if (
          row.height + rowOffsetY + pageHeight > height ||
          this.rowList[i - 1]?.isPageBreak
        ) {
          if (Number.isInteger(maxPageNo) && pageNo >= maxPageNo!) {
            // 跨页表格片段共享元素索引：按片段边界裁剪表格，
            // 保留已展示片段内容，不能直接按共享索引整体截断
            const fragment = row.tableFragment
            const tableElement = this.elementList[row.startIndex]
            if (
              fragment &&
              tableElement?.type === ElementType.TABLE &&
              this.tablePaging.truncateTableByFragment(
                tableElement,
                fragment,
                this.elementList
              )
            ) {
              this.elementList = this.elementList.slice(0, row.startIndex + 1)
            } else {
              this.elementList = this.elementList.slice(0, row.startIndex)
            }
            break
          }
          pageNo++
          pageHeight = this.getMainOuterHeight(pageNo) + row.height + rowOffsetY
          pageRowList.push([row])
        } else {
          pageHeight += row.height + rowOffsetY
          pageRowList[pageNo].push(row)
        }
        prevColumnIndex = row.columnIndex
      }
    }
    return pageRowList
  }

  private _drawHighlight(ctx2d: Context2d, payload: IDrawRowPayload) {
    const {
      control: { activeBackgroundColor }
    } = this.options
    const { rowList, positionList /*, elementList */ } = payload
    const marginHeight = this.getDefaultBasicRowMarginHeight()
    const highlightMarginHeight = this.getHighlightMarginHeight()
    // const activeControlElement = this.control.getActiveControl()?.getElement()
    for (let i = 0; i < rowList.length; i++) {
      const curRow = rowList[i]
      for (let j = 0; j < curRow.elementList.length; j++) {
        const element = curRow.elementList[j]
        if (element.highlight) {
          // const preElement = curRow.elementList[j - 1]
          //     if (
          //       element.highlight ||
          //       (activeBackgroundColor &&
          //         activeControlElement &&
          //         element.controlId === activeControlElement.controlId &&
          //         !this.control.getIsRangeInPostfix())
          //     ) {
          //       // 高亮元素相连需立即绘制，并记录下一元素坐标
          //       if (
          //         preElement &&
          //         preElement.highlight &&
          //         preElement.highlight !== element.highlight
          //       ) {
          //         this.highlight.render(ctx2d)
          //       }
          // 当前元素位置信息记录（表格跨页片段行优先使用片段位置）
          const {
            coordinate: {
              leftTop: [x, y]
            }
          } = curRow.fragmentPosition || positionList[curRow.startIndex + j]
          // 元素向左偏移量
          const offsetX = element.left || 0
          this.highlight.recordFillInfo(
            ctx2d,
            x - offsetX,
            y + marginHeight - highlightMarginHeight, // 先减去行margin，再加上高亮margin
            element.metrics.width + offsetX,
            curRow.height - 2 * marginHeight + 2 * highlightMarginHeight,
            element.highlight || activeBackgroundColor
          )
          //     } else
          // if (preElement?.highlight) {
          //   // 之前是高亮元素，当前不是需立即绘制
          this.highlight.render(ctx2d)
          // }
        }
      }
      // this.highlight.render(ctx2d)
    }
  }

  public drawRow(ctx2d: Context2d, payload: IDrawRowPayload) {
    // 优先绘制高亮元素
    this._drawHighlight(ctx2d, payload)
    // 绘制元素、下划线、删除线、选区
    const {
      scale,
      table: { tdPadding },
      group,
      lineBreak
    } = this.options
    const {
      rowList,
      pageNo,
      // elementList,
      positionList,
      // startIndex,
      zone,
      isDrawLineBreak = !lineBreak.disabled
    } = payload
    const isPrintMode = this.isPrintMode()
    // const isGraffitiMode = this.isGraffitiMode()
    // const { isCrossRowCol } = this.range.getRange()
    // let index = startIndex
    // let offY = 0
    for (let i = 0; i < rowList.length; i++) {
      const curRow = rowList[i]
      // 选区绘制记录
      // const rangeRecord: IElementFillRect = {
      //   x: 0,
      //   y: 0,
      //   width: 0,
      //   height: 0
      // }
      // let tableRangeElement: IElement | null = null
      for (let j = 0; j < curRow.elementList.length; j++) {
        const element = curRow.elementList[j]
        const metrics = element.metrics
        // 当前元素位置信息（表格跨页片段行优先使用片段位置）
        const {
          ascent: offsetY,
          coordinate: {
            leftTop: [x, y]
          }
        } = curRow.fragmentPosition || positionList[curRow.startIndex + j]
        // offY += y
        const preElement = curRow.elementList[j - 1]
        // 元素绘制
        if (
          (element.hide || element.control?.hide || element.area?.hide) &&
          !this.isDesignMode()
        ) {
          // 控件隐藏时不绘制
          this.textParticle.complete()
        } else if (element.type === ElementType.IMAGE) {
          this.textParticle.complete()
          // 浮动图片单独绘制
          if (
            element.imgDisplay !== ImageDisplay.SURROUND &&
            element.imgDisplay !== ImageDisplay.FLOAT_TOP &&
            element.imgDisplay !== ImageDisplay.FLOAT_BOTTOM
          ) {
            this.imageParticle.render(this.getCtx2d(), element, x, y + offsetY)
          }
        } else if (element.type === ElementType.LATEX) {
          this.textParticle.complete()
          this.laTexParticle.render(this.getCtx2d(), element, x, y + offsetY)
        } else if (element.type === ElementType.TABLE) {
          // if (isCrossRowCol) {
          //   rangeRecord.x = x
          //   rangeRecord.y = y
          //   // tableRangeElement = element
          // }
          this.tableParticle.render(
            this.getCtx2d(),
            element,
            x,
            y,
            curRow.tableFragment
          )
        } else if (element.type === ElementType.HYPERLINK) {
          this.textParticle.complete()
          this.hyperlinkParticle.render(
            this.getCtx2d(),
            element,
            x,
            y + offsetY
          )
        } else if (element.type === ElementType.LABEL) {
          this.textParticle.complete()
          this.labelParticle.render(this.getCtx2d(), element, x, y + offsetY)
        } else if (element.type === ElementType.DATE) {
          const nextElement = curRow.elementList[j + 1]
          // 释放之前的
          if (!preElement || preElement.dateId !== element.dateId) {
            this.textParticle.complete()
          }
          this.textParticle.record(this.getCtx2d(), element, x, y + offsetY)
          if (!nextElement || nextElement.dateId !== element.dateId) {
            // 手动触发渲染
            this.textParticle.complete()
          }
        } else if (element.type === ElementType.SUPERSCRIPT) {
          this.textParticle.complete()
          this.superscriptParticle.render(
            this.getCtx2d(),
            element,
            x,
            y + offsetY
          )
        } else if (element.type === ElementType.SUBSCRIPT) {
          this.underline.render(this.getCtx2d())
          this.textParticle.complete()
          this.subscriptParticle.render(
            this.getCtx2d(),
            element,
            x,
            y + offsetY
          )
        } else if (element.type === ElementType.SEPARATOR) {
          this.separatorParticle.render(this.getCtx2d(), element, x, y)
        } else if (element.type === ElementType.PAGE_BREAK) {
          // if (this.mode !== EditorMode.CLEAN && !isPrintMode) {
          //   this.pageBreakParticle.render(this.getCtx2d(), element, x, y)
          // }
        } else if (
          element.type === ElementType.CHECKBOX ||
          element.controlComponent === ControlComponent.CHECKBOX
        ) {
          this.textParticle.complete()
          this.checkboxParticle.render({
            ctx2d: this.getCtx2d(),
            x,
            y: y + offsetY,
            index: j,
            row: curRow
          })
        } else if (
          element.type === ElementType.RADIO ||
          element.controlComponent === ControlComponent.RADIO
        ) {
          this.textParticle.complete()
          this.radioParticle.render({
            ctx2d: this.getCtx2d(),
            x,
            y: y + offsetY,
            index: j,
            row: curRow
          })
        } else if (element.type === ElementType.TAB) {
          this.textParticle.complete()
        } else if (
          element.rowFlex === RowFlex.ALIGNMENT ||
          element.rowFlex === RowFlex.JUSTIFY
        ) {
          // 如果是两端对齐，因canvas目前不支持letterSpacing需单独绘制文本
          this.textParticle.record(this.getCtx2d(), element, x, y + offsetY)
          this.textParticle.complete()
        } else if (element.type === ElementType.BLOCK) {
          this.textParticle.complete()
          this.blockParticle.render(ctx2d, pageNo, element, x, y + offsetY)
        } else {
          // 如果当前元素设置左偏移，则上一元素立即绘制
          if (element.left) {
            this.textParticle.complete()
          }
          // console.log('element')
          // console.log(element)
          // if (element.rowFlex)
          this.textParticle.record(this.getCtx2d(), element, x, y + offsetY)
          // 如果设置字宽、字间距、标点符号（避免浏览器排版缩小间距）需单独绘制
          if (
            element.width ||
            element.letterSpacing ||
            PUNCTUATION_REG.test(element.value)
          ) {
            this.textParticle.complete()
          }
        }
        // 换行符绘制
        if (
          isDrawLineBreak &&
          !isPrintMode &&
          this.mode !== EditorMode.CLEAN &&
          !curRow.isWidthNotEnough &&
          j === curRow.elementList.length - 1
        ) {
          this.lineBreakParticle.render(
            this.getCtx2d(),
            element,
            x,
            y + curRow.height / 2
          )
        }
        // // 边框绘制（目前仅支持控件）
        // if (element.control?.border) {
        //   // 不同控件边框立刻绘制
        //   if (
        //     preElement?.control?.border &&
        //     preElement.controlId !== element.controlId
        //   ) {
        //     this.control.drawPdfBorder(this.getCtx2d())
        //   }
        //   // 当前元素位置信息记录
        //   const rowMargin = this.getElementRowMargin(element)
        //   this.control.recordBorderInfo(
        //     x,
        //     y + rowMargin,
        //     element.metrics.width,
        //     curRow.height - 2 * rowMargin
        //   )
        // } else if (preElement?.control?.border) {
        //   this.control.drawPdfBorder(this.getCtx2d())
        // }
        // 下划线记录
        if (element.underline || element.control?.underline) {
          // 下标元素下划线单独绘制
          if (
            preElement?.type === ElementType.SUBSCRIPT &&
            element.type !== ElementType.SUBSCRIPT
          ) {
            this.underline.render(this.getCtx2d())
          }
          // 行间距
          const rowMargin = this.getElementRowMargin(element)
          // 元素向左偏移量
          const offsetX = element.left || 0
          // 下标元素y轴偏移值
          let offsetY = 0
          if (element.type === ElementType.SUBSCRIPT) {
            offsetY = this.subscriptParticle.getOffsetY(element)
          }
          // 占位符不参与颜色计算
          const color =
            element.controlComponent === ControlComponent.PLACEHOLDER
              ? undefined
              : element.color
          this.underline.recordFillInfo(
            this.getCtx2d(),
            x - offsetX,
            y + curRow.height - rowMargin + offsetY,
            metrics.width + offsetX,
            0,
            color,
            element.textDecoration?.style
          )
        } else if (preElement?.underline || preElement?.control?.underline) {
          this.underline.render(this.getCtx2d())
        }
        // 删除线记录
        if (element.strikeout) {
          // 仅文本类元素支持删除线
          if (!element.type || TEXTLIKE_ELEMENT_TYPE.includes(element.type)) {
            // 字体大小不同时需立即绘制
            if (
              preElement &&
              ((preElement.type === ElementType.SUBSCRIPT &&
                element.type !== ElementType.SUBSCRIPT) ||
                (preElement.type === ElementType.SUPERSCRIPT &&
                  element.type !== ElementType.SUPERSCRIPT) ||
                this.getElementSize(preElement) !==
                  this.getElementSize(element))
            ) {
              this.strikeout.render(this.getCtx2d())
            }
            // 基线文字测量信息
            const standardMetrics = this.textParticle.measureBasisWord(
              this.getCtx2d(),
              this.getFont(element)
            )
            // 文字渲染位置 + 基线文字下偏移量 - 一半文字高度
            let adjustY =
              y +
              offsetY +
              standardMetrics.actualBoundingBoxDescent * scale -
              metrics.height / 2
            // 上下标位置调整
            if (element.type === ElementType.SUBSCRIPT) {
              adjustY += this.subscriptParticle.getOffsetY(element)
            } else if (element.type === ElementType.SUPERSCRIPT) {
              adjustY += this.superscriptParticle.getOffsetY(element)
            }
            this.strikeout.recordFillInfo(
              this.getCtx2d(),
              x,
              adjustY,
              metrics.width
            )
          }
        } else if (preElement?.strikeout) {
          this.strikeout.render(this.getCtx2d())
        }
        // // 选区记录
        // const {
        //   zone: currentZone,
        //   startIndex,
        //   endIndex
        // } = this.range.getRange()
        // if (
        //   currentZone === zone &&
        //   startIndex !== endIndex &&
        //   startIndex <= index &&
        //   index <= endIndex
        // ) {
        //   const positionContext = this.position.getPositionContext()
        //   // 表格需限定上下文
        //   if (
        //     (!positionContext.isTable && !element.tdId) ||
        //     positionContext.tdId === element.tdId
        //   ) {
        //     // 从行尾开始-绘制最小宽度
        //     if (startIndex === index) {
        //       const nextElement = elementList[startIndex + 1]
        //       if (nextElement && nextElement.value === ZERO) {
        //         rangeRecord.x = x + metrics.width
        //         rangeRecord.y = y
        //         rangeRecord.height = curRow.height
        //         rangeRecord.width += this.options.rangeMinWidth
        //       }
        //     } else {
        //       let rangeWidth = metrics.width
        //       // 最小选区宽度
        //       if (rangeWidth === 0 && curRow.elementList.length === 1) {
        //         rangeWidth = this.options.rangeMinWidth
        //       }
        //       // 记录第一次位置、行高
        //       if (!rangeRecord.width) {
        //         rangeRecord.x = x
        //         rangeRecord.y = y
        //         rangeRecord.height = curRow.height
        //       }
        //       rangeRecord.width += rangeWidth
        //     }
        //   }
        // }
        // 组信息记录
        if (!group.disabled && element.groupIds) {
          this.group.recordFillInfo(element, x, y, metrics.width, curRow.height)
        }
        // index++
        // 绘制表格内元素
        if (element.type === ElementType.TABLE) {
          const tdPaddingWidth = tdPadding[1] + tdPadding[3]
          const fragment = curRow.tableFragment
          // 续页回显表头内容（使用一次性位置列表）
          if (curRow.repeatTdPositionList?.length) {
            for (const {
              td,
              positionList: repeatPositionList
            } of curRow.repeatTdPositionList) {
              this.drawRow(ctx2d, {
                elementList: td.value,
                positionList: repeatPositionList,
                rowList: td.rowList!,
                pageNo,
                startIndex: 0,
                innerWidth: (td.width! - tdPaddingWidth) * scale,
                zone,
                isDrawLineBreak
              })
            }
          }
          // 遍历片段范围行与进位合并单元格，仅绘制窗口内的内容行
          const fragmentTdList = fragment
            ? this.tableParticle.getFragmentTdList(element, fragment)
            : element.trList!.flatMap(tr => tr.tdList)
          for (const td of fragmentTdList) {
            let rowList = td.rowList!
            let startIndex = 0
            if (fragment) {
              const [windowStart, windowEnd] =
                this.tableParticle.getTdWindowInFragment(td, element, fragment)
              if (windowEnd <= windowStart) continue
              if (windowStart > 0 || windowEnd < td.height!) {
                const visible = this.tableParticle.getTdVisibleRowListByWindow(
                  td,
                  windowStart,
                  windowEnd
                )
                rowList = visible.rowList
                startIndex = visible.startIndex
              }
            }
            this.drawRow(ctx2d, {
              elementList: td.value,
              positionList: td.positionList!,
              rowList,
              pageNo,
              startIndex,
              innerWidth: (td.width! - tdPaddingWidth) * scale,
              zone,
              isDrawLineBreak
            })
          }
        }
      }
      // 绘制列表样式
      if (curRow.isList) {
        this.listParticle.drawListStyle(
          ctx2d,
          curRow,
          positionList[curRow.startIndex]
        )
      }
      // 绘制文字、边框、下划线、删除线
      this.textParticle.complete()
      // this.control.drawPdfBorder(this.getCtx2d())
      this.underline.render(this.getCtx2d())
      this.strikeout.render(this.getCtx2d())
      // 绘制批注样式
      this.group.render(this.getCtx2d())
      // // 绘制选区
      // if (!isPrintMode && !isGraffitiMode) {
      //   if (rangeRecord.width && rangeRecord.height) {
      //     const { x, y, width, height } = rangeRecord
      //     this.range.render(ctx, x, y, width, height)
      //   }
      //   if (
      //     isCrossRowCol &&
      //     tableRangeElement &&
      //     tableRangeElement.id === tableId
      //   ) {
      //     const {
      //       coordinate: {
      //         leftTop: [x, y]
      //       }
      //     } = positionList[curRow.startIndex]
      //     this.tableParticle.drawRange(ctx, tableRangeElement, x, y)
      //   }
      // }
    }
  }

  private _drawFloat(ctx2d: Context2d, payload: IDrawFloatPayload) {
    const { scale } = this.options
    const floatPositionList = this.position.getFloatPositionList()
    const { imgDisplays, pageNo } = payload
    for (let e = 0; e < floatPositionList.length; e++) {
      const floatPosition = floatPositionList[e]
      const element = floatPosition.element
      if (
        (pageNo === floatPosition.pageNo ||
          floatPosition.zone === EditorZone.HEADER ||
          floatPosition.zone == EditorZone.FOOTER) &&
        element.imgDisplay &&
        imgDisplays.includes(element.imgDisplay) &&
        element.type === ElementType.IMAGE
      ) {
        const imgFloatPosition = element.imgFloatPosition!
        this.imageParticle.render(
          ctx2d,
          element,
          imgFloatPosition.x * scale,
          imgFloatPosition.y * scale
        )
      }
    }
  }

  private _clearPage(pageNo: number) {
    const ctx = this.getCtx2d()
    const pageDom = this.getPageList()[pageNo]
    if (pageDom) {
      ctx.clearRect(
        0,
        0,
        Math.max(pageDom.width, this.getWidth()),
        Math.max(pageDom.height, this.getHeight())
      )
    }
    this.blockParticle.clear()
  }

  private _drawPage(payload: IDrawPagePayload) {
    const { elementList, positionList, rowList, pageNo } = payload
    const {
      // inactiveAlpha,
      pageMode,
      header,
      footer,
      pageNumber,
      lineNumber,
      pageBorder
    } = this.options
    const isPrintMode = this.mode === EditorMode.PRINT
    const isContinuityMode = pageMode === PageMode.CONTINUITY
    const innerWidth = this.getInnerWidth()
    // const ctx = this.ctxList[pageNo]
    const ctx2d = this.getCtx2d()
    this.getPdf().setPage(pageNo + 1)
    // 判断当前激活区域-非正文区域时元素透明度降低
    // ctx2d.globalAlpha = !this.zone.isMainActive() ? inactiveAlpha : 1
    this._clearPage(pageNo)
    // 绘制背景
    if (
      !isPrintMode ||
      !this.options.modeRule[EditorMode.PRINT]?.backgroundDisabled
    ) {
      this.background.render(ctx2d, pageNo)
    }
    // 绘制区域
    if (!isPrintMode) {
      this.area.render(ctx2d, pageNo)
    }
    // 绘制水印（底层）
    if (
      !isContinuityMode &&
      this.options.watermark.data &&
      this.options.watermark.layer === WatermarkLayer.BOTTOM
    ) {
      this.waterMark.render(ctx2d, pageNo)
    }
    // 绘制页边距
    if (!isPrintMode) {
      this.margin.render(ctx2d, pageNo)
    }
    // 绘制分栏分隔线
    this.columnManager.drawSeparator(ctx2d, pageNo)
    // 渲染衬于文字下方元素
    this._drawFloat(ctx2d, {
      pageNo,
      imgDisplays: [ImageDisplay.FLOAT_BOTTOM]
    })
    // 控件高亮
    // this.control.renderHighlightList(ctx2d, pageNo)
    // 渲染元素
    const index = rowList[0]?.startIndex
    this.drawRow(ctx2d, {
      elementList,
      positionList,
      rowList,
      pageNo,
      startIndex: index,
      innerWidth,
      zone: EditorZone.MAIN
    })
    // if (this.getIsPagingMode()) {
    if (this.getOptions().pageMode === PageMode.PAGING) {
      // 绘制页眉
      if (!header.disabled) {
        this.header.render(ctx2d, pageNo)
      }
      // 绘制页码
      if (!pageNumber.disabled) {
        this.pageNumber.render(ctx2d, pageNo)
      }
      // 绘制页脚
      if (!footer.disabled) {
        this.footer.render(ctx2d, pageNo)
      }
    }
    // 渲染浮于文字上方元素
    this._drawFloat(ctx2d, {
      pageNo,
      imgDisplays: [ImageDisplay.FLOAT_TOP, ImageDisplay.SURROUND]
    })
    // // 搜索匹配绘制
    // if (this.search.getSearchKeyword()) {
    //   this.search.render(ctx, pageNo)
    // }
    // 绘制空白占位符
    if (this.elementList.length <= 1 && !this.elementList[0]?.listId) {
      this.placeholder.render(ctx2d)
    }
    // 渲染行数
    if (!lineNumber.disabled) {
      this.lineNumber.render(ctx2d, pageNo)
    }
    // 绘制页面边框
    if (!pageBorder.disabled) {
      this.pageBorder.render(ctx2d)
    }
    // 绘制签章
    this.badge.render(ctx2d, this.elementList[0], pageNo)
    // 绘制涂鸦
    if (this.isGraffitiMode()) {
      this.graffiti.render(ctx2d, pageNo)
    }
    // 绘制水印（顶层）
    if (
      !isContinuityMode &&
      this.options.watermark.data &&
      this.options.watermark.layer === WatermarkLayer.TOP
    ) {
      this.waterMark.render(ctx2d, pageNo)
    }
  }

  private _immediateRender() {
    const positionList = this.getPosition().getOriginalMainPositionList()
    const elementList = this.getOriginalMainElementList()
    for (let i = 0; i < this.pageRowList.length; i++) {
      this._drawPage({
        elementList,
        positionList,
        rowList: this.pageRowList[i],
        pageNo: i
      })
    }
  }

  private _syncPageCount() {
    const targetPageCount = this.pageRowList.length
    for (let i = 0; i < targetPageCount; i++) {
      if (!this.pageList[i]) {
        this._createPage(i)
      }
    }
    if (this.pageList.length > targetPageCount) {
      this.pageList.splice(targetPageCount).forEach(page => {
        if (page.remove) {
          page.remove()
        }
      })
    }
    const pdfPageCount = this.getPdf().getNumberOfPages()
    if (pdfPageCount > targetPageCount) {
      for (let i = pdfPageCount; i > targetPageCount; i--) {
        this.getPdf().deletePage(i)
      }
    } else if (pdfPageCount < targetPageCount) {
      for (let i = pdfPageCount; i < targetPageCount; i++) {
        this.getPdf().addPage()
      }
    }
  }

  public render(payload?: IDrawOption) {
    // Resets the content of jsPDF pages to prevent successive renders
    // from stacking operators in the content stream (causing PDF size to increase)
    // with each export when reusing the same instance.
    this._resetPdf()
    const { header, footer } = this.options
    const { isCompute = true } = payload || {}
    const innerWidth = this.getInnerWidth()
    const isPagingMode = this.getOptions().pageMode === PageMode.PAGING
    // 缓存当前页数信息
    // 计算文档信息
    if (isCompute) {
      // 清空浮动元素位置信息
      this.position.setFloatPositionList([])
      if (isPagingMode) {
        // 分栏信息
        this.columnManager.compute()
        // 页眉信息
        if (!header.disabled) {
          this.header.compute()
        }
        // 页脚信息
        if (!footer.disabled) {
          this.footer.compute()
        }
      }
      // 行信息
      const margins = this.getMargins()
      const pageHeight = this.getHeight()
      const extraHeight = this.header.getExtraHeight()
      const startX = margins[3]
      const startY = margins[0] + extraHeight
      const surroundElementList = pickSurroundElementList(this.elementList)
      this.rowList = this.computeRowList({
        startX,
        startY,
        pageHeight,
        isPagingMode,
        innerWidth,
        surroundElementList,
        elementList: this.elementList
      })!
      // 分页模式下跨页表格在渲染层拆分为按页片段行
      if (isPagingMode) {
        this.rowList = this.tablePaging.splitTableRowAcrossPages(this.rowList)
      }
      // 页面信息
      this.pageRowList = this._computePageList() //this.draw.getPageRowList()
      // 位置信息
      this.position.computePositionList()
      // 区域信息
      this.area.compute()
      // 涂鸦信息
      if (this.isGraffitiMode()) {
        this.graffiti.compute()
      }
      // // 控件关键词高亮
      // this.control.computeHighlightList()
    }
    // 创建纸张
    this._syncPageCount()
    this._immediateRender()
  }

  // Recreates the jsPDF instance with each render to prevent internal state accumulation
  // between exports (e.g., the putImage bug in jsPDF that mutates the global filter array
  // via splice, causing the 2nd save onwards to exit without
  // FlatEncode and ~3x larger). Fonts loaded via downloadFont are
  // reapplied from fontCache in the new instance.
  private _resetPdf() {
    this.pdf = new jsPDF({
      orientation: 'p',
      unit: 'px',
      format: [this.getWidth(), this.getHeight()],
      hotfixes: ['px_scaling'],
      compress: true
    })
    this.pdf.setDocumentProperties({
      author: 'canvas-editor'
    })
    this.fontCache.forEach(f => this._applyFont(this.pdf, f))
    this.pageList = []
    this.ctxListInfos = []
  }

  public destroy() {
    // this.container.remove()
  }

  public clearSideEffect() {
    // this.getTableTool().dispose()
    this.getHyperlinkParticle().clearHyperlinkPopup()
    // æ¥ææ§ä»¶
    this.getDateParticle().clearDatePicker()
  }
}
